<template>
    <div v-if="locations?.length">
        <div v-if="showHeader" class="contact-us-segment__title">
            {{ $t('pages.contact.contact.contact') }}
        </div>
        <div class="contact-us-segment__wrapper">
            <div class="contact-us-segment__data">
                <common-content-spacer height="medium">
                    <select-input
                        v-if="locations.length > 1"
                        :label="$t('components.reservations.reservation_list.table.titles.location')"
                        :options="locationOptions"
                        :prefillVal="selectedLocationOption"
                        @change="locationChange"
                    ></select-input>
                    <div class="contact-us-segment__container">
                        <common-icon
                            class="contact-us-segment__container--icon"
                            name="location-point"
                            size="medium"
                        ></common-icon>
                        <div class="contact-us-segment__container--content">
                            <div class="contact-us-segment__heading">{{ locationProviderName }}</div>
                            <div>{{ selectedLocation?.address }}</div>
                            <div>{{ selectedLocation?.city?.name }}</div>
                        </div>
                    </div>
                    <div v-if="selectedLocation?.emails[0].email" class="contact-us-segment__container">
                        <common-icon class="contact-us-segment__container--icon" name="email" size="medium"></common-icon>
                        <div class="contact-us-segment__container--content">
                            <div v-if="!isEmailShow" class="contact-us-segment__click" @click="showEmail">
                                {{ $t('components.user.user_card.click_email') }}
                            </div>
                            <div v-for="(email, index) in selectedLocation?.emails" :key="index">
                                <a v-if="isEmailShow" class="contact-us-segment__link" :href="`mailto:${email.email}`">
                                    {{ email.email }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div v-if="selectedLocation?.companyServiceProvider?.subdomain" class="contact-us-segment__container">
                        <common-icon class="contact-us-segment__container--icon" name="url" size="semi-small"></common-icon>
                        <div class="contact-us-segment__container--content">
                            <div class="contact-us-segment__click">
                                <a :href="hostURL" target="_blank">{{ hostURL }}</a>
                            </div>
                        </div>
                    </div>

                    <div v-if="selectedLocation?.telephones[0].telephone" class="contact-us-segment__container">
                        <common-icon class="contact-us-segment__container--icon" name="phone" size="medium"></common-icon>
                        <div class="contact-us-segment__container--content">
                            <div v-if="!isPhoneShow" class="contact-us-segment__click" @click="showPhone">
                                {{ $t('components.user.user_card.click_phone') }}
                            </div>
                            <div v-for="(telephone, index) in selectedLocation?.telephones" :key="index">
                                <a v-if="isPhoneShow" class="contact-us-segment__link" :href="`tel:${telephone.telephone}`">
                                    {{ telephone.telephone }}
                                </a>
                            </div>
                        </div>
                    </div>
                    <div class="contact-us-segment__container contact-us-segment__container--social">
                        <a v-if="selectedLocation?.facebook" :href="selectedLocation.facebook" target="_blank">
                            <common-icon
                                class="contact-us-segment__container--social contact-us-segment__container--social--facebook"
                                name="facebook"
                                size="huge"
                            ></common-icon>
                        </a>
                        <a v-if="selectedLocation?.instagram" :href="selectedLocation.instagram" target="_blank">
                            <common-icon
                                class="contact-us-segment__container--social contact-us-segment__container--social--instagram"
                                name="instagram"
                                size="huge"
                            ></common-icon>
                        </a>
                        <a v-if="selectedLocation?.twitter" :href="selectedLocation.twitter" target="_blank">
                            <common-icon
                                class="contact-us-segment__container--social contact-us-segment__container--social-twitter"
                                name="x-twitter"
                                size="huge"
                            ></common-icon>
                        </a>
                        <a v-if="selectedLocation?.linkedin" :href="selectedLocation.linkedin" target="_blank">
                            <common-icon
                                class="contact-us-segment__container--social contact-us-segment__container--social--linkedin"
                                name="linkedin"
                                size="huge"
                            ></common-icon>
                        </a>
                        <a v-if="selectedLocation?.tiktok" :href="selectedLocation.tiktok" target="_blank">
                            <common-icon
                                class="contact-us-segment__container--social contact-us-segment__container--social--tiktok"
                                name="tiktok"
                                size="huge"
                            ></common-icon>
                        </a>
                    </div>
                </common-content-spacer>
            </div>
            <div class="contact-us-segment__map">
                <map-component
                    :showCurrentLocation="showCurrentLocation"
                    :selectedLocation="selectedLocation"
                    :markers="markers"
                    :mapCenter="mapCenter"
                    :defaultZoom="15"
                ></map-component>
            </div>
        </div>
    </div>
</template>

<script>
    import MapComponent from 'components/page-elements/locations/map/MapComponent'
    import SelectInput from 'components/form-inputs/SelectInput'
    import CommonContentSpacer from '../../common/page/CommonContentSpacer.vue'

    export default {
        components: { MapComponent, SelectInput, CommonContentSpacer },

        props: {
            markers: { type: Array, default: () => new Array() },
            locations: { type: Array, default: () => new Array() },
            showCurrentLocation: { type: Boolean, default: false },
            showHeader: { type: Boolean, default: true },
        },

        data: () => ({
            selectedLocationOption: undefined,
            isEmailShow: false,
            isPhoneShow: false,
        }),

        computed: {
            selectedLocation() {
                return this.locations?.find((location) => location?.id === this.selectedLocationOption)
            },

            locationProviderName() {
                return (
                    this.selectedLocation?.companyServiceProvider?.name ||
                    `${this.selectedLocation?.userServiceProvider?.firstName} ${this.selectedLocation?.userServiceProvider?.lastName}`
                )
            },

            mapCenter() {
                return this.selectedLocation
                    ? { lat: this.selectedLocation.latitude, lng: this.selectedLocation.longitude }
                    : undefined
            },

            locationOptions() {
                return this.locations.map((location) => ({
                    value: location?.id,
                    label: `${location?.address}, ${location?.city?.name}`,
                }))
            },
            hostURL() {
                var firstSegmentOfUrl = window.location.host.split('.')

                return firstSegmentOfUrl[0] === this.selectedLocation?.companyServiceProvider.subdomain
                    ? `${process.env.VUE_APP_URL_PROTOCOL}${window.location.host}`
                    : `${process.env.VUE_APP_URL_PROTOCOL}${this.selectedLocation?.companyServiceProvider.subdomain}.${window.location.host}`
            },
        },

        methods: {
            locationChange(e) {
                this.selectedLocationOption = e.value
            },

            showEmail() {
                this.isEmailShow = true
            },

            showPhone() {
                this.isPhoneShow = true
            },

            setDefaultSelectedLocation() {
                if (this.locationOptions.length && this.locationOptions[0]?.value) {
                    this.selectedLocationOption = this.locationOptions[0].value
                }
            },
        },

        watch: {
            locationOptions() {
                this.setDefaultSelectedLocation()
            },
        },

        mounted() {
            this.setDefaultSelectedLocation()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/components/common/_contact-us-segment.scss';
</style>
